// src/modals/DeleteAccountModal.jsx
// Phase 40 Plan 04 Task 1 — typed-DELETE gate per UI-SPEC #7. AU-05.
// CONTEXT D-17. RESEARCH.md Pattern 8.
//
// Standalone component (clone of the Phase 31.1 typed-DELETE shape) — copy
// verbatim from UI-SPEC #7. Strict equality on the typed string (no trim,
// no case-fold). The modal owns its own input + in-flight states; the
// caller passes async onConfirm and toggles inProgress externally if it
// wants the "Deleting…" label change to render across renders.

import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

export default function DeleteAccountModal({ plantsCount, onConfirm, onCancel, inProgress = false, errorMessage = null }) {
  const inputRef = useRef(null);
  const cancelRef = useRef(null);
  const triggerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  const [inputValue, setInputValue] = useState('');

  const canConfirm = inputValue === 'DELETE' && !inProgress;

  useEffect(() => {
    inputRef.current?.focus();
    const handleKey = (e) => {
      if (e.key === 'Escape' && !inProgress) { e.preventDefault(); onCancel(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      try { triggerRef.current?.focus?.(); } catch { /* noop */ }
    };
  }, [onCancel, inProgress]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'var(--c-overlay)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-modal-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--c-bg)', border: '1px solid var(--c-danger-border)', borderRadius: 16, padding: 28, maxWidth: 360, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}
      >
        <h2 id="delete-account-modal-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'var(--font-h2)', color: 'var(--c-heading)', margin: '0 0 12px', fontWeight: 600 }}>
          Delete your Tend account
        </h2>
        <p style={{ fontSize: 'var(--font-body-size)', color: 'var(--c-text)', margin: '0 0 16px', lineHeight: 1.55 }}>
          This will permanently delete: your account, all <em>{plantsCount}</em> plants, all areas, all completion records, all notes, and any photos you've uploaded. This can't be undone.
        </p>
        <div
          style={{ background: 'var(--c-danger-bg)', border: '1px solid var(--c-danger-border)', borderRadius: 10, padding: 16, marginBottom: 16 }}
        >
          <small style={{ color: 'var(--c-danger)', fontSize: 'var(--font-small)', lineHeight: 1.55 }}>
            Once deleted, signing back in will start you fresh — there's no recovery.
          </small>
        </div>
        <label
          htmlFor="delete-account-modal-input"
          style={{ display: 'block', fontSize: 'var(--font-body-size)', color: 'var(--c-text)', marginBottom: 8 }}
        >
          Type DELETE to confirm:
        </label>
        <input
          id="delete-account-modal-input"
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder="DELETE"
          disabled={inProgress}
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${inputValue === 'DELETE' ? 'var(--c-danger-border)' : 'var(--c-border)'}`,
            fontSize: 'var(--font-input)',
            width: '100%',
            boxSizing: 'border-box',
            background: 'var(--c-bg)',
            color: 'var(--c-text)',
            marginBottom: 16,
          }}
          aria-describedby="delete-account-modal-title"
        />
        {errorMessage && (
          <small style={{ display: 'block', color: 'var(--c-danger)', marginBottom: 12, fontSize: 'var(--font-small)' }}>
            {errorMessage}
          </small>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            onClick={canConfirm ? onConfirm : undefined}
            disabled={!canConfirm}
            style={{
              background: 'var(--c-danger)',
              color: 'var(--c-danger-text)',
              border: 'none',
              borderRadius: 20,
              padding: '12px 22px',
              fontSize: 'var(--font-body-size)',
              fontWeight: 600,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: canConfirm ? 1 : 0.5,
              minHeight: 44,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontFamily: 'inherit',
            }}
          >
            {inProgress ? (<><Loader2 size={16} style={{ animation: 'tendSpin 1s linear infinite' }} /> Deleting…</>) : 'Delete account permanently'}
          </button>
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={inProgress}
            style={{
              background: 'var(--c-btn-bg)',
              color: 'var(--c-btn-text)',
              border: 'none',
              borderRadius: 20,
              padding: '10px 22px',
              fontSize: 'var(--font-body-size)',
              fontWeight: 600,
              cursor: inProgress ? 'not-allowed' : 'pointer',
              opacity: inProgress ? 0.5 : 1,
              minHeight: 44,
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
